/**
 * Safe path utilities for @scope.path expressions.
 *
 * Format: "@<scope>.<segment>.<segment>..."
 * Segments may use dot or bracket notation: "@items[0].name" or "@items.0.name".
 */

export interface ParsedPath {
  scope: string;
  segments: readonly string[];
}

const PATH_REGEX = /^@([a-zA-Z_$][a-zA-Z0-9_$]*)(.*)$/;

export function parsePath(expr: string): ParsedPath | null {
  if (typeof expr !== "string" || !expr.startsWith("@")) return null;
  const match = PATH_REGEX.exec(expr.trim());
  if (!match) return null;
  const scope = match[1] ?? "";
  const tail = match[2] ?? "";
  const segments: string[] = [];
  // Tokenize ".key" and "[index]" forms
  const tokenRe = /\.([a-zA-Z_$][a-zA-Z0-9_$]*)|\[(\d+)\]|\["([^"]+)"\]|\['([^']+)'\]/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(tail)) !== null) {
    if (m.index !== cursor) return null; // gap = invalid
    cursor = m.index + m[0].length;
    const seg = m[1] ?? m[2] ?? m[3] ?? m[4];
    if (seg !== undefined) segments.push(seg);
  }
  if (cursor !== tail.length) return null;
  return { scope, segments };
}

export function readPath(
  root: Record<string, unknown>,
  segments: readonly string[],
): unknown {
  let cur: unknown = root;
  for (const seg of segments) {
    if (cur === null || cur === undefined) return undefined;
    if (typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

export function writePath(
  root: Record<string, unknown>,
  segments: readonly string[],
  value: unknown,
): void {
  if (segments.length === 0) return;
  let cur: Record<string, unknown> = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i] as string;
    const next = cur[seg];
    if (next === null || next === undefined || typeof next !== "object") {
      cur[seg] = {};
    }
    cur = cur[seg] as Record<string, unknown>;
  }
  cur[segments[segments.length - 1] as string] = value;
}

export function deletePath(
  root: Record<string, unknown>,
  segments: readonly string[],
): void {
  if (segments.length === 0) return;
  let cur: Record<string, unknown> = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i] as string;
    const next = cur[seg];
    if (!next || typeof next !== "object") return;
    cur = next as Record<string, unknown>;
  }
  delete cur[segments[segments.length - 1] as string];
}
